import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, useIsViewMode,
} from './detail/DetailShell';

// /api/Roles controller'ı (Add/Update/Delete + GetById?id= + OData GET).
// Kayıt görüntüleme/düzenleme artık tam-sayfa detaya (/role-management/:id) gider — RolePage liste-only.
const API_BASE = '/api/Roles';
const MENU_API = '/api/Menu';
const ROLERIGHT_API = '/api/RoleRight';
const USERROLE_API = '/api/UserRoles';
const PAGEACTION_API = '/api/PageRoleActionRights';
// #276/#900 Menü bazlı aksiyon hakları (ActionType enum ile birebir): Yeni=1, Düzenle=2, Sil=3, Kopyala=4, İzle=17.
const ACTION_RIGHTS = [
  { id: 1, label: 'Yeni' },
  { id: 2, label: 'Düzenle' },
  { id: 3, label: 'Sil' },
  { id: 4, label: 'Kopyala' },
  { id: 17, label: 'İzle' },
];
const actKey = (pageId, atId) => `${pageId}:${atId}`;
const VIEW_ACTION = 17;

// Yetki tutarlılık kuralları (bu ekranda ZORLANIR):
//   K1  Aksiyon ⇒ İzle    : Yeni/Düzenle/Sil/Kopyala işaretlenince İzle otomatik açılır.
//                           İzle kaldırılınca o menüdeki TÜM aksiyonlar düşer (göremediğin
//                           kaydı silemezsin). K1, "menü görünür ama İzle yok → 403" çelişkisini
//                           kökünden bitirir: 403 yalnız menüde bazı aksiyonlar işaretliyken
//                           İzle işaretsizse oluşuyordu.
//   K2  Aksiyon ⇒ Menü    : Bir aksiyon işaretlenince menü (ve ataları) görünür yapılır.
//   K3  Menü kapalı ⇒ hak yok : Menü görünürlüğü kaldırılınca o menülerin aksiyon hakları silinir.
//   K4  İzle → Menü senkron   : İzle kaldırılınca menü de görünmez olur.
// BİLEREK YAPILMAYAN ters yön: menü işaretlemek İzle satırı YAZMAZ. Backend'de bir menüde tek
// satır bile olması "artık kısıtlı" demektir; menüyü işaretlemek 17 satırı yazsaydı o menü
// yalnız-izle'ye düşer, hâlihazırda default-open (hiç satırı yok = tüm aksiyonlar açık) olan
// menülerde Yeni/Düzenle/Sil sessizce kapanırdı.
// ADMIN rolü (kod 01) GetMyMenu'da RoleRight filtresini bypass eder → tüm menüyü görür.
// Bu yüzden yetki sekmesi ADMIN'de salt-okunur gösterilir (yanlışlıkla kısıtlamayı önler).
const ADMIN_ROLE_CODE = '01';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = { code: '', name: '', isActive: 'true' };

// #287/#900 Menü yetkileri tablo stilleri — aksiyon sütunları sabit genişlik = hizalı.
const RIGHTS_ACTION_COL = 66;
const rightsNameHead = {
  textAlign: 'left', padding: '8px 8px 8px 2px', position: 'sticky', top: 0, zIndex: 1,
  background: 'var(--dps-surface, #fff)', borderBottom: '2px solid var(--dps-line, #e5e7eb)',
  fontSize: 12, fontWeight: 700, color: 'var(--dps-ink-2, #475569)',
};
const rightsActionHead = {
  ...rightsNameHead, textAlign: 'center', width: RIGHTS_ACTION_COL, fontSize: 11,
  color: 'var(--dps-ink-3, #64748b)',
};
const rightsNameCell = { padding: '3px 8px 3px 2px', borderBottom: '1px solid var(--dps-line, #eef2f7)' };
const rightsActionCell = {
  padding: '3px 0', textAlign: 'center', width: RIGHTS_ACTION_COL,
  borderBottom: '1px solid var(--dps-line, #eef2f7)',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

// --- Menü öğesi alan erişimcileri (OData casing PascalCase/camelCase olabilir) ---
const mId = (m) => Number(pick(m, 'id', 'Id'));
const mParent = (m) => { const v = pick(m, 'parentID', 'ParentID', 'parentId', 'ParentId'); return v == null ? null : Number(v); };
const mActive = (m) => (pick(m, 'isActive', 'IsActive') ?? true) !== false;
const mLabel = (m) => pick(m, 'title', 'Title', 'name', 'Name') ?? `#${mId(m)}`;
const mSort = (m) => Number(pick(m, 'sortOrder', 'SortOrder') ?? 0);

function buildMenuTree(flat) {
  const active = flat.filter(mActive);
  const childrenOf = (pid) => active
    .filter((m) => mParent(m) === pid)
    .sort((a, b) => mSort(a) - mSort(b))
    .map((n) => ({ id: mId(n), label: mLabel(n), children: childrenOf(mId(n)) }));
  return childrenOf(null);
}
function collectIds(node) { return [node.id, ...node.children.flatMap(collectIds)]; }
// #900 Bir düğümün altındaki YAPRAK (alt menüsü olmayan) menü id'leri. Aksiyon hakları
// yalnız yaprak sayfalarda tutulur; başlık menü aksiyonu bunlara cascade eder.
function leafIdsOf(node) {
  return node.children.length === 0 ? [node.id] : node.children.flatMap(leafIdsOf);
}

function rowToForm(r) {
  return {
    code: pick(r, 'code', 'Code') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    isActive: (pick(r, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
  };
}

export function RoleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  usePageMeta(isNew ? 'Yeni Rol' : 'Rol Detayı', 'Rol kaydı kod, ad ve durum bilgileri.');
  // #287/#900 Menü aksiyon yetkisi — hak yoksa ilgili düğmeleri gizle (kısıt yoksa default-open).
  // Mevcut isView / isAdminRole davranışı KORUNUR; yetki bunun üzerine AND'lenir.
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Menü yetkileri sekmesi state'i
  const [menus, setMenus] = useState([]);
  const [rights, setRights] = useState(() => new Set());
  // #276 Menü bazlı aksiyon hakları — Set of "pageId:actionTypeId".
  const [actionRights, setActionRights] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [rightsLoading, setRightsLoading] = useState(false);

  // #279 Bu role sahip kullanıcılar sekmesi
  const [roleUsers, setRoleUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const isAdminRole = (form.code || '').trim() === ADMIN_ROLE_CODE;
  // #282 İzle modu (liste göz/çift-tık) → salt-okunur. Admin de zaten salt-okunur.
  const isView = useIsViewMode() && !isNew;
  const rightsReadOnly = isAdminRole || isView; // menü/aksiyon onay kutuları kilitli
  const formReadOnly = (isAdminRole && !isNew) || isView; // form alanları kilitli

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // Önce listeden taşınan satır (location.state.row) varsa onu kullan.
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      // Nav-state satırı liste OData $select'inden gelir; audit alanlarını (createdDate/By…)
      // İÇERMEZ → info butonu gizli kalır. Tam kaydı arka planda çekip audit'i ondan doldur
      // (paint'i bloklamaz).
      fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rol alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Rol alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Menü listesi + (mevcut rolse) atanmış RoleRight'ları yükle.
  const loadMenusAndRights = useCallback(async () => {
    setRightsLoading(true);
    try {
      const menuRes = await fetch(`${MENU_API}?$orderby=SortOrder asc&$top=1000`, { headers: authHeaders() });
      const menuRows = menuRes.ok ? getODataRows(await menuRes.json()) : [];
      setMenus(menuRows);
      // Varsayılan: ana grupları (parentID null) açık göster.
      setExpanded(new Set(menuRows.filter((m) => mParent(m) == null).map(mId)));
      if (!isNew) {
        const rrRes = await fetch(`${ROLERIGHT_API}?$filter=RoleId eq ${id}&$top=2000`, { headers: authHeaders() });
        const rrRows = rrRes.ok ? getODataRows(await rrRes.json()) : [];
        setRights(new Set(rrRows.map((x) => Number(pick(x, 'menuId', 'MenuId')))));
        // #276 Bu rolün menü-aksiyon haklarını yükle (IsEnable olanlar).
        const paRes = await fetch(`${PAGEACTION_API}?$filter=RoleId eq ${id}&$top=5000`, { headers: authHeaders() });
        const paRows = paRes.ok ? getODataRows(await paRes.json()) : [];
        setActionRights(new Set(paRows
          .filter((x) => (pick(x, 'isEnable', 'IsEnable') ?? false))
          .map((x) => actKey(Number(pick(x, 'pageId', 'PageId')), Number(pick(x, 'actionTypeId', 'ActionTypeId'))))));
      }
    } catch {
      setMenus([]);
    } finally {
      setRightsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { loadMenusAndRights(); }, [loadMenusAndRights]);

  // #279 "Kullanıcılar" sekmesi: bu role atanmış kullanıcıları çek (UserRoles→userId, sonra Users).
  // NOT: /api/UserRoles?$expand=User backend'de asıldığından iki-adımlı güvenli sorgu.
  useEffect(() => {
    if (tab !== 'users' || isNew || !id) return;
    let active = true;
    setUsersLoading(true);
    (async () => {
      try {
        const urRes = await fetch(`${USERROLE_API}?$filter=RoleId eq ${id}&$top=1000`, { headers: authHeaders() });
        const urRows = urRes.ok ? getODataRows(await urRes.json()) : [];
        const userIds = [...new Set(urRows.map((x) => Number(pick(x, 'userId', 'UserId'))).filter(Boolean))];
        if (!userIds.length) { if (active) setRoleUsers([]); return; }
        const filter = userIds.map((u) => `Id eq ${u}`).join(' or ');
        const uRes = await fetch(`/api/Users?$filter=${encodeURIComponent(filter)}&$top=1000`, { headers: authHeaders() });
        const uRows = uRes.ok ? getODataRows(await uRes.json()) : [];
        if (active) setRoleUsers(uRows);
      } catch {
        if (active) setRoleUsers([]);
      } finally {
        if (active) setUsersLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, isNew]);

  // Klonla: /role-management/new'e kopya veriyle gelindiyse formu doldur.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const menuTree = useMemo(() => buildMenuTree(menus), [menus]);
  // Bir menünün tüm atalarını (parent zinciri) bulmak için harita — kaydederken
  // seçilen yaprağın grup başlıkları da işaretlenir ki GetMyMenu ağacında görünsün.
  const parentMap = useMemo(() => {
    const map = new Map();
    menus.forEach((m) => map.set(mId(m), mParent(m)));
    return map;
  }, [menus]);
  /**
   * Seçilen menülerin ATALARINI da kümeye ekler (yaprak görünürse üst grubu da görünmeli).
   *
   * ⚠ Yalnız GERÇEKTEN VAR OLAN menüler eklenir. Eskiden ata id'si `parentMap`te karşılığı
   * olup olmadığına bakılmadan ekleniyordu: `dbo.Menu`da ebeveyni artık bulunmayan (öksüz)
   * bir satır varsa o dangling id `RoleRight`e yazılmaya çalışılıyor ve kayıt
   * "The INSERT statement conflicted with the FOREIGN KEY constraint FK_RoleRight_Menu_MenuId"
   * ile patlıyordu — kullanıcı hangi menünün sorun çıkardığını göremeden.
   *
   * Aynı tuzak menü listesi $top ile kesildiğinde de kurulur: yüklenmemiş bir ebeveyn
   * "bilinmeyen" olur. Var olma kontrolü ikisini birden kapatır.
   */
  const withAncestors = useCallback((idSet) => {
    const out = new Set(idSet);
    idSet.forEach((startId) => {
      let p = parentMap.get(startId);
      // Döngü koruması: bozuk veride ebeveyn zinciri kendine dönebilir.
      const seen = new Set();
      while (p != null && parentMap.has(p) && !seen.has(p)) {
        seen.add(p);
        out.add(p);
        p = parentMap.get(p);
      }
    });
    return out;
  }, [parentMap]);

  const nodeState = useCallback((node) => {
    const ids = collectIds(node);
    const on = ids.filter((i) => rights.has(i)).length;
    if (on === 0) return 'off';
    return on === ids.length ? 'on' : 'partial';
  }, [rights]);

  const toggleNode = (node) => {
    if (rightsReadOnly) return;
    const ids = collectIds(node);
    const turningOff = ids.every((i) => rights.has(i));
    setRights((prev) => {
      const next = new Set(prev);
      if (turningOff) ids.forEach((i) => next.delete(i));
      else ids.forEach((i) => next.add(i));
      return next;
    });
    // K3: menü görünürlüğü kalktıysa o menülerdeki aksiyon hakları da düşer — aksi halde
    // görünmeyen bir ekran için Sil/Düzenle hakkı kayıtlı kalırdı.
    if (turningOff) {
      const leaves = leafIdsOf(node);
      setActionRights((prev) => {
        const next = new Set(prev);
        leaves.forEach((l) => ACTION_RIGHTS.forEach((a) => next.delete(actKey(l, a.id))));
        return next;
      });
    }
  };
  const toggleExpand = (nodeId) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });
  const selectAll = () => setRights(new Set(menus.filter(mActive).map(mId)));
  const clearAll = () => { setRights(new Set()); setActionRights(new Set()); };

  // Tüm yaprak (alt menüsü olmayan) menü id'leri — sütun "tümünü seç" ve cascade için.
  const allLeafIds = useMemo(() => menuTree.flatMap(leafIdsOf), [menuTree]);

  // #276 Bir menü satırı için aksiyon hakkının (Sil/Düzenle/İzle/Kopyala) durumu.
  // #900 Başlık menüde durum, alt yaprakların topluca durumudur (on/partial/off).
  const nodeActionState = useCallback((node, atId) => {
    const leaves = leafIdsOf(node);
    if (leaves.length === 0) return 'off';
    const on = leaves.filter((l) => actionRights.has(actKey(l, atId))).length;
    if (on === 0) return 'off';
    return on === leaves.length ? 'on' : 'partial';
  }, [actionRights]);

  // #276/#900 Aksiyon hakkını aç/kapa. Yaprakta tek sayfayı, başlıkta tüm alt yaprakları
  // (cascade) etkiler: hepsi açıksa hepsini kapat, değilse hepsini aç (toggle).
  const toggleNodeAction = (node, atId) => {
    if (rightsReadOnly) return;
    const leaves = leafIdsOf(node);
    if (leaves.length === 0) return;
    const turningOff = leaves.every((l) => actionRights.has(actKey(l, atId)));
    setActionRights((prev) => {
      const next = new Set(prev);
      if (turningOff) {
        // K1: İzle kalkıyorsa o menüde hiçbir aksiyon kalamaz; diğer aksiyonlar tek tek düşer.
        if (atId === VIEW_ACTION) leaves.forEach((l) => ACTION_RIGHTS.forEach((a) => next.delete(actKey(l, a.id))));
        else leaves.forEach((l) => next.delete(actKey(l, atId)));
      } else {
        leaves.forEach((l) => {
          next.add(actKey(l, atId));
          if (atId !== VIEW_ACTION) next.add(actKey(l, VIEW_ACTION)); // K1
        });
      }
      return next;
    });
    setRights((prev) => {
      // K4: İzle kalktı → menü de görünmez. Başlık satırında toggleNode ile aynı kapsam
      // (başlık + tüm alt düğümleri) düşer, yoksa çocuğu kalmamış boş grup görünür kalırdı.
      if (turningOff) {
        if (atId !== VIEW_ACTION) return prev;
        const next = new Set(prev);
        collectIds(node).forEach((i) => next.delete(i));
        return next;
      }
      // K2: aksiyon verildi → menü (ve ataları) görünür olmalı.
      const next = new Set(prev);
      leaves.forEach((l) => next.add(l));
      return withAncestors(next);
    });
  };

  // #287 Sütun başlığı "tümünü seç": ilgili aksiyonu tüm yaprak menülerde aç/kapa (toggle).
  const columnState = useCallback((atId) => {
    if (allLeafIds.length === 0) return 'off';
    const on = allLeafIds.filter((l) => actionRights.has(actKey(l, atId))).length;
    if (on === 0) return 'off';
    return on === allLeafIds.length ? 'on' : 'partial';
  }, [allLeafIds, actionRights]);

  const toggleColumn = (atId) => {
    if (rightsReadOnly) return;
    const turningOff = allLeafIds.every((l) => actionRights.has(actKey(l, atId)));
    setActionRights((prev) => {
      const next = new Set(prev);
      if (turningOff) {
        // K1 sütun genelinde: İzle sütunu kapanırsa hiçbir menüde aksiyon kalamaz.
        if (atId === VIEW_ACTION) allLeafIds.forEach((l) => ACTION_RIGHTS.forEach((a) => next.delete(actKey(l, a.id))));
        else allLeafIds.forEach((l) => next.delete(actKey(l, atId)));
      } else {
        allLeafIds.forEach((l) => {
          next.add(actKey(l, atId));
          if (atId !== VIEW_ACTION) next.add(actKey(l, VIEW_ACTION)); // K1
        });
      }
      return next;
    });
    setRights((prev) => {
      // K4 sütun genelinde: İzle sütunu kapandı → hiçbir menü görünmez (başlıklar dahil).
      if (turningOff) return atId === VIEW_ACTION ? new Set() : prev;
      const next = new Set(prev);            // K2
      allLeafIds.forEach((l) => next.add(l));
      return withAncestors(next);
    });
  };

  // #900 Ağacı, açık düğümleri izleyerek görünür satır dizisine düzleştir (tablo render'ı için).
  const visibleRows = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => {
      nodes.forEach((n) => {
        out.push({ node: n, depth });
        if (n.children.length && expanded.has(n.id)) walk(n.children, depth + 1);
      });
    };
    walk(menuTree, 0);
    return out;
  }, [menuTree, expanded]);

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.code.trim()) { showToast('error', 'Rol kodu zorunludur'); return; }
    if (!form.name.trim()) { showToast('error', 'Rol adı zorunludur'); return; }
    setSaving(true);
    try {
      // Menü yetkileri: ADMIN'de gönderme (null → backend dokunmaz, zaten tüm menüyü görür).
      // Diğer rollerde seçili menü id'leri + ataları gönderilir. pageRoleActionRight HİÇ
      // gönderilmez (null) → backend mevcut aksiyon haklarını korur (bu ekran onları yönetmez).
      let roleRightField = {};
      if (!isAdminRole) {
        const roleIdVal = isNew ? 0 : Number(id);

        // Kaydetmeden önce tutarlılık kurallarını (K1/K2) veriye de uygula. Ekrandaki toggle'lar
        // bunları zaten korur, ama DB'de ESKİ tutarsız kayıt olabilir (ör. İzle'siz Sil): kullanıcı
        // o satıra hiç dokunmadan kaydederse çelişki aynen geri yazılırdı. Burada normalize edilir.
        const normalizedActions = new Set(actionRights);
        const menuIdsFromActions = new Set();
        [...actionRights].forEach((k) => {
          const [pageId] = k.split(':').map(Number);
          normalizedActions.add(actKey(pageId, VIEW_ACTION)); // K1: aksiyon ⇒ İzle
          menuIdsFromActions.add(pageId);                     // K2: aksiyon ⇒ menü görünür
        });

        const finalIds = [...withAncestors(new Set([...rights, ...menuIdsFromActions]))];
        roleRightField = {
          roleRight: finalIds.map((menuId) => ({ roleId: roleIdVal, menuId })),
          // #276 Menü aksiyon hakları — "pageId:actionTypeId" set'inden üretilir; backend full-replace.
          pageRoleActionRight: [...normalizedActions].map((k) => {
            const [pageId, atId] = k.split(':').map(Number);
            return { pageId, actionTypeId: atId, roleId: roleIdVal, isEnable: true };
          }),
        };
      }
      const payload = isNew
        ? { code: form.code, name: form.name, isActive: true, ...roleRightField }
        : { id: Number(id), code: form.code, name: form.name, isActive: form.isActive === 'true', ...roleRightField };
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Rol eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setRights(new Set());
          setActionRights(new Set());
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Rol başarıyla eklendi');
        setTimeout(() => navigate('/role-management'), 400);
        return;
      }
      showToast('success', 'Rol başarıyla güncellendi');
      setTimeout(() => navigate('/role-management'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => { navigate('/role-management/new', { state: { clone: { ...form } } }); };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: Number(id) }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Rol başarıyla silindi');
      setTimeout(() => navigate('/role-management'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (
    <>
      {form.isActive === 'true'
        ? <StatusBadge tone="ok">Aktif</StatusBadge>
        : <StatusBadge tone="neutral">Pasif</StatusBadge>}
      {/* #277 ADMIN rolü açıkça işaretlenir. */}
      {isAdminRole && <StatusBadge tone="wait">Yönetici (Admin)</StatusBadge>}
    </>
  );

  // #277/#282 ADMIN veya İzle modunda salt-okunur; "Kapat" (+ izlede admin değilse "Düzenle").
  const actions = formReadOnly && !isNew ? (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/role-management')}>Kapat</button>
      {!isAdminRole && canEdit && (
        <button className="dps-btn primary" onClick={() => navigate(`/role-management/${id}`, { state: { row: location.state?.row, view: false }, replace: true })}>Düzenle</button>
      )}
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/role-management')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={canCopy ? [{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni rol aç',
            icon: 'copy',
            onClick: handleClone,
          }] : []}
        />
      ))}
    </>
  );

  // #900 Menü adı ile aksiyon kutuları tablo hücrelerine oturur → sütunlar her satırda hizalı.
  const renderRow = ({ node, depth }) => {
    const st = nodeState(node);
    const hasKids = node.children.length > 0;
    const isOpen = expanded.has(node.id);
    return (
      <tr key={node.id}>
        <td style={rightsNameCell}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: depth * 22 }}>
            {hasKids ? (
              <button type="button" onClick={() => toggleExpand(node.id)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', padding: 2, color: 'inherit' }}
                aria-label={isOpen ? 'Kapat' : 'Aç'}>
                <Icon name={isOpen ? 'chevD' : 'chevR'} size={14} />
              </button>
            ) : <span style={{ width: 18, display: 'inline-block' }} />}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: rightsReadOnly ? 'default' : 'pointer', fontWeight: hasKids ? 600 : 400 }}>
              <input
                type="checkbox"
                checked={st === 'on'}
                ref={(el) => { if (el) el.indeterminate = st === 'partial'; }}
                disabled={rightsReadOnly}
                onChange={() => toggleNode(node)}
              />
              <span>{node.label}</span>
            </label>
          </div>
        </td>
        {/* #276/#900 Her satırda (başlık dahil) Sil/Düzenle/İzle/Kopyala kutusu. Başlıkta
            durum alt yaprakların birleşimi (indeterminate) ve tıklama cascade eder. */}
        {ACTION_RIGHTS.map((a) => {
          const ast = nodeActionState(node, a.id);
          return (
            <td key={a.id} style={rightsActionCell}>
              <input
                type="checkbox"
                title={a.label}
                checked={ast === 'on'}
                ref={(el) => { if (el) el.indeterminate = ast === 'partial'; }}
                disabled={rightsReadOnly}
                onChange={() => toggleNodeAction(node, a.id)}
              />
            </td>
          );
        })}
      </tr>
    );
  };

  const rightsHeaderActions = !rightsReadOnly && (
    <div style={{ display: 'flex', gap: 8 }}>
      <button type="button" className="dps-btn ghost" onClick={selectAll}>Tümünü Seç</button>
      <button type="button" className="dps-btn ghost" onClick={clearAll}>Temizle</button>
    </div>
  );

  return (
    <DetailShell
      readOnly={formReadOnly}
      crumb={[{ label: 'Rol Tanımları', to: '/role-management' }, { label: isNew ? 'Yeni Rol' : (isView ? 'Rol Görüntüle' : 'Rol Detayı'), current: true }]}
      status={statusBadge}
      tabs={[
        { key: 'info', label: 'Rol Bilgileri', icon: 'shield' },
        { key: 'rights', label: 'Menü Yetkileri', icon: 'list' },
        { key: 'users', label: 'Kullanıcılar', icon: 'users', disabled: isNew, title: isNew ? 'Önce rolü kaydedin' : undefined },
      ]}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Role', entityId: id }}
      loading={loading}
    >
      {tab === 'info' && (
        <div className="dps-form">
          <Section icon="shield" title="Rol Bilgileri" subtitle="Rol kodu, adı ve durum bilgileri">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="code" label="Rol Kodu" req mono />
              <FieldText form={form} set={set} tried={tried} k="name" label="Rol Adı" req />
              <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            </Grid>
            {/* #277 Admin göstergesi — bilgi amaçlı, salt-okunur (rol kodu 01 ise otomatik). */}
            {isAdminRole && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'default' }}>
                <input type="checkbox" checked readOnly disabled style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Yönetici (Admin) rolü — tüm menülere tam erişim; bu rol düzenlenemez.</span>
              </label>
            )}
          </Section>
        </div>
      )}

      {tab === 'rights' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Menü Yetkileri"
            subtitle="Sol onay kutusu menünün görünürlüğü; sağdaki Yeni/Düzenle/Sil/Kopyala/İzle o menüdeki işlem haklarıdır. Bir işlem hakkı verildiğinde İzle ve menü görünürlüğü otomatik açılır; İzle kaldırılınca o menüdeki tüm haklar düşer ve menü gizlenir. Hiçbir kutusu işaretlenmemiş menüde kısıt yoktur — o menüde tüm işlemler serbesttir."
            right={rightsHeaderActions}
          >
            {isAdminRole && (
              <p style={{ margin: '0 0 12px', padding: '10px 12px', borderRadius: 8, background: 'var(--dps-info-bg, #eef2ff)', lineHeight: 1.5 }}>
                <strong>ADMIN</strong> rolü tüm menüleri görür; menü yetkileri bu rolde kısıtlanamaz (salt-okunur).
              </p>
            )}
            {rightsLoading ? (
              <div className="dps-loading">Menüler yükleniyor…</div>
            ) : menuTree.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.7 }}>Menü bulunamadı.</p>
            ) : (
              <div style={{ maxHeight: 560, overflow: 'auto', paddingRight: 4 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={rightsNameHead}>Menü</th>
                      {/* #287 Sütun başlığındaki kutu o aksiyonu tüm satırlarda topluca aç/kapatır. */}
                      {ACTION_RIGHTS.map((a) => {
                        const cs = columnState(a.id);
                        return (
                          <th key={a.id} style={rightsActionHead}>
                            <label style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: rightsReadOnly ? 'default' : 'pointer' }} title={`Tüm menülerde ${a.label}`}>
                              <span>{a.label}</span>
                              <input
                                type="checkbox"
                                checked={cs === 'on'}
                                ref={(el) => { if (el) el.indeterminate = cs === 'partial'; }}
                                disabled={rightsReadOnly}
                                onChange={() => toggleColumn(a.id)}
                              />
                            </label>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      )}

      {tab === 'users' && (
        <div className="dps-form">
          <Section icon="users" title="Bu Role Sahip Kullanıcılar" subtitle="Bu role atanmış kullanıcılar (salt-okunur).">
            {usersLoading ? (
              <div className="dps-loading">Kullanıcılar yükleniyor…</div>
            ) : roleUsers.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.7 }}>Bu role atanmış kullanıcı yok.</p>
            ) : (
              <table className="dps-table">
                <thead><tr><th>Ad Soyad</th><th>E-posta</th><th style={{ width: 90 }} /></tr></thead>
                <tbody>
                  {roleUsers.map((u) => {
                    const uid = pick(u, 'id', 'Id');
                    const first = pick(u, 'firstName', 'FirstName') ?? '';
                    const last = pick(u, 'lastName', 'LastName') ?? '';
                    const email = pick(u, 'email', 'Email') ?? '';
                    return (
                      <tr key={uid}>
                        <td><strong>{`${first} ${last}`.trim() || '-'}</strong></td>
                        <td>{email || '-'}</td>
                        <td className="dps-td-actions">
                          <button type="button" className="dps-row-btn" onClick={() => navigate(`/user-management/${uid}`)}>Aç</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> rolünü silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RoleDetailPage;
